/**
 * resolveUserAvatar
 *
 * Single source of truth for determining which image URL to display as a
 * user's avatar throughout the platform.
 *
 * Priority chain (highest → lowest):
 *   1. profilePicture — uploaded custom profile image (Asset object, uses .url)
 *   2. gravatar       — Gravatar URL. Always present for registered users, so
 *                       this is the effective fallback for anyone without an
 *                       upload.
 *   3. fallback       — caller-supplied default (e.g. CDN placeholder)
 *
 * Note: nftProfilePicture is VESTIGIAL — NFT avatars were retired; it remains on
 * the type only because old payloads still carry it.
 *
 * Accepts a loose shape so the function works with full User objects from
 * @devour/client as well as leaderboard entries, friend objects, and any
 * other partial user shapes that carry a subset of these fields.
 *
 * Note: profilePicture is typed as Asset (an object with a .url field) on the
 * generated User model. All other avatar fields are plain string URLs.
 */
export interface UserAvatarFields {
    nftProfilePicture?: string | null;
    profilePicture?: { url?: string } | string | null;
    gravatar?: string | null;
}

/**
 * Canonical default-avatar placeholder. Single source of truth for the
 * "no avatar of any kind" image. Served from the web app's own /public
 * origin (NOT a CDN) so it is identical across every environment and can
 * never depend on which CDN bucket happens to have the file.
 *
 * Never hardcode a per-environment CDN avatar URL — always resolve through this.
 */
export const DEFAULT_AVATAR = "/images/devourplay-profile.webp";

/**
 * Returns the highest-priority avatar URL for a user, or the fallback if none
 * are available.
 *
 * @param user     - Any object that may contain avatar fields. Safe to pass
 *                   undefined/null (returns fallback).
 * @param fallback - URL to return when the user has no avatar of any kind.
 *                   Defaults to an empty string so callers can detect the
 *                   absence and render an icon instead.
 * @param options  - Optional flags:
 *                   - skipGravatar: when true, skip the gravatar fallback in
 *                     the chain. Use when the caller's UX wants to render
 *                     branded initials / placeholder for users without an
 *                     uploaded profilePicture, instead of falling back to
 *                     the email-MD5 gravatar identicon. RTTR / Minelab and
 *                     other brand-themed surfaces opt in here so the
 *                     no-upload state reads as their brand color, not as
 *                     a generic gravatar tile.
 */
export function resolveUserAvatar(
    user: UserAvatarFields | null | undefined,
    fallback = "",
    options: { skipGravatar?: boolean } = {},
): string {
    if (!user) return fallback;

    if (user.profilePicture) {
        // profilePicture is Asset on the full User model, plain string on some leaderboard shapes
        const url = typeof user.profilePicture === "string"
            ? user.profilePicture
            : user.profilePicture.url;
        if (url) return url;
    }

    if (!options.skipGravatar && user.gravatar) return user.gravatar;

    return fallback;
}
